import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const RoleBasedHeader = ({ userRole = 'customer', userName = 'User', onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  const navigationItems = {
    customer: [
      { path: '/service-booking-wizard', label: 'Réserver un service', icon: 'Calendar' },
      { path: '/public-service-tracking', label: 'Suivre ma demande', icon: 'MapPin' },
      { path: '/customer-account-portal', label: 'Mon compte', icon: 'User' },
    ],
    technician: [
      { path: '/technician-daily-routes', label: 'Mes interventions', icon: 'Route' },
      { path: '/public-service-tracking', label: 'Suivi en direct', icon: 'MapPin' },
    ],
    dispatcher: [
      { path: '/dispatch-control-center', label: 'Centre de dispatch', icon: 'Radio' },
      { path: '/service-booking-wizard', label: 'Nouvelle demande', icon: 'Plus' },
      { path: '/analytics-dashboard', label: 'Tableau de bord', icon: 'BarChart3' },
    ],
    admin: [
      { path: '/dispatch-control-center', label: 'Dispatch', icon: 'Radio' },
      { path: '/service-catalog-manager', label: 'Catalogue', icon: 'Package' },
      { path: '/analytics-dashboard', label: 'Analytics', icon: 'BarChart3' },
      { path: '/customer-account-portal', label: 'Comptes', icon: 'Users' },
    ],
    trainer: [
      { path: '/service-catalog-manager', label: 'Catalogue de services', icon: 'Package' },
      { path: '/technician-daily-routes', label: 'Parcours techniciens', icon: 'Route' },
      { path: '/analytics-dashboard', label: 'Rapports', icon: 'FileText' },
    ],
  };

  const currentNavItems = navigationItems?.[userRole] || navigationItems?.customer;

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login-authentication');
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const quickActions = {
    dispatcher: [
      { icon: 'Calendar', label: 'Planning', onClick: () => console.log('Planning') },
      { icon: 'MapPin', label: 'Carte', onClick: () => console.log('Carte') },
      { icon: 'Package', label: 'Stock', onClick: () => navigate('/inventory-management') },
      { icon: 'BarChart3', label: 'Analytics', onClick: () => navigate('/analytics-dashboard') }
    ],
  };

  return (
    <>
      <header className="nav-header">
        <div className="nav-header-container">
          <div className="nav-header-logo">
            <div className="nav-header-logo-icon">
              <Icon name="Droplet" size={24} color="#FFFFFF" />
            </div>
            <span className="nav-header-logo-text">PlumberPro</span>
          </div>

          <nav className="nav-header-menu">
            {currentNavItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`nav-header-menu-item ${isActivePath(item?.path) ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            ))}
          </nav>

          <div className="nav-header-actions">
            <div className={`nav-header-status ${connectionStatus}`}>
              <span className="nav-header-status-dot"></span>
              <span>
                {connectionStatus === 'online' ? 'En ligne' : 
                 connectionStatus === 'offline'? 'Hors ligne' : 'Connexion...'}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              iconName="User"
              className="hidden lg:flex"
            >
              {userName}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              iconName="LogOut"
              onClick={handleLogout}
              className="hidden lg:flex"
            >
              Déconnexion
            </Button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="nav-header-mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <>
          <div
            className="nav-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="nav-mobile-menu">
            <div className="nav-mobile-menu-header">
              <div className="flex items-center gap-3">
                <div className="nav-header-logo-icon">
                  <Icon name="Droplet" size={20} color="#FFFFFF" />
                </div>
                <span className="font-semibold text-foreground">PlumberPro</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="nav-mobile-menu-close"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="nav-mobile-menu-items">
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="User" size={16} />
                  <span className="text-sm font-medium">{userName}</span>
                </div>
                <div className={`status-indicator ${connectionStatus}`}>
                  <span className="status-indicator-dot"></span>
                  <span>
                    {connectionStatus === 'online' ? 'En ligne' : 
                     connectionStatus === 'offline'? 'Hors ligne' : 'Connexion...'}
                  </span>
                </div>
              </div>

              {currentNavItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`nav-mobile-menu-item ${isActivePath(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </button>
              ))}

              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={handleLogout}
                  className="nav-mobile-menu-item text-error hover:bg-error/10"
                >
                  <Icon name="LogOut" size={20} />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default RoleBasedHeader;